import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Editor } from '@tinymce/tinymce-react';
import MessageBox from './MessageBox';
import ConfirmDialog from './ConfirmDialog';

const API_BASE_URL = window.API_BASE_URL;

export default function SavedPapers({ currentUser }) {
  const editorRef = useRef(null);
  const downloadMenuRef = useRef(null);
  const downloadButtonRef = useRef(null);
  const [downloadMenuPos, setDownloadMenuPos] = useState(null);
  const [papers, setPapers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ show: false, text: '', type: 'info' });
  const [selectedPaper, setSelectedPaper] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState({
    title: '',
    class: '',
    date: '',
    subject: '',
    content: ''
  });
  const [confirm, setConfirm] = useState({ show: false, text: '', onConfirm: null });
  const [searchTerm, setSearchTerm] = useState('');
  const [downloadDropdownOpen, setDownloadDropdownOpen] = useState(null); // Track which paper's dropdown is open
  const [filterClass, setFilterClass] = useState('');
  const [filterSubject, setFilterSubject] = useState('');
  const [filterTeacher, setFilterTeacher] = useState('');
  const [filterMode, setFilterMode] = useState('all'); // 'all' or 'today'
  const [allPapers, setAllPapers] = useState([]);

  useEffect(() => {
    loadPapers();
  }, []);

  const closeDownloadMenu = () => {
    setDownloadDropdownOpen(null);
    setDownloadMenuPos(null);
  };

  const openDownloadMenu = (paperId, buttonEl) => {
    if (downloadDropdownOpen === paperId) {
      closeDownloadMenu();
      return;
    }
    const rect = buttonEl.getBoundingClientRect();
    const menuHeight = 88;
    const menuWidth = 180;
    const spaceBelow = window.innerHeight - rect.bottom;
    const top = spaceBelow < menuHeight + 12
      ? Math.max(8, rect.top - menuHeight - 4)
      : rect.bottom + 4;
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - menuWidth - 8));
    setDownloadMenuPos({ top, left });
    setDownloadDropdownOpen(paperId);
  };

  // Close dropdown when clicking outside the menu. Ignore presses inside it,
  // otherwise the option click is swallowed and no download starts.
  useEffect(() => {
    if (downloadDropdownOpen === null) return undefined;

    const handlePointerDown = (event) => {
      const target = event.target;
      if (downloadMenuRef.current && downloadMenuRef.current.contains(target)) return;
      if (downloadButtonRef.current && downloadButtonRef.current.contains(target)) return;
      closeDownloadMenu();
    };

    document.addEventListener('mousedown', handlePointerDown);
    window.addEventListener('resize', closeDownloadMenu);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      window.removeEventListener('resize', closeDownloadMenu);
    };
  }, [downloadDropdownOpen]);

  const loadPapers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      console.log('Loading papers from:', `${API_BASE_URL}/subjective-papers`);
      
      const response = await fetch(`${API_BASE_URL}/subjective-papers`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('Response status:', response.status);
      const data = await response.json();
      console.log('Response data:', data);

      if (!response.ok) {
        throw new Error(data.message || 'Failed to load papers');
      }

      console.log('Papers received:', data.papers?.length || 0);
      setAllPapers(data.papers || []);
      setPapers(data.papers || []);
    } catch (error) {
      console.error('Error loading papers:', error);
      setMessage({ show: true, text: error.message || 'Failed to load papers', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleView = async (paper) => {
    setSelectedPaper(paper);
    setIsEditing(false);
  };

  const handleEdit = (paper) => {
    setEditFormData({
      title: paper.title,
      class: paper.class,
      date: paper.date ? new Date(paper.date).toISOString().split('T')[0] : '',
      subject: paper.subject,
      content: paper.content
    });
    setSelectedPaper(paper);
    setIsEditing(true);
  };

  const handleSaveEdit = async () => {
    if (!editFormData.title || !editFormData.class || !editFormData.date || !editFormData.subject) {
      setMessage({ show: true, text: 'Please fill in all fields', type: 'error' });
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      
      const response = await fetch(`${API_BASE_URL}/subjective-papers/${selectedPaper.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(editFormData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to update paper');
      }

      setMessage({ show: true, text: 'Paper updated successfully!', type: 'success' });
      setIsEditing(false);
      setSelectedPaper(null);
      loadPapers();
    } catch (error) {
      console.error('Error updating paper:', error);
      setMessage({ show: true, text: error.message || 'Failed to update paper', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (paper) => {
    setConfirm({
      show: true,
      text: `Are you sure you want to delete "${paper.title}"?`,
      onConfirm: async () => {
        setLoading(true);
        try {
          const token = localStorage.getItem('authToken');
          
          const response = await fetch(`${API_BASE_URL}/subjective-papers/${paper.id}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          const data = await response.json();

          if (!response.ok) {
            throw new Error(data.message || 'Failed to delete paper');
          }

          setMessage({ show: true, text: 'Paper deleted successfully!', type: 'success' });
          loadPapers();
        } catch (error) {
          console.error('Error deleting paper:', error);
          setMessage({ show: true, text: error.message || 'Failed to delete paper', type: 'error' });
        } finally {
          setLoading(false);
          setConfirm({ show: false, text: '', onConfirm: null });
        }
      }
    });
  };

  const handleDownload = async (paper, format = 'legal') => {
    try {
      console.log('Downloading paper:', paper.id, paper.title, 'Format:', format);
      const token = localStorage.getItem('authToken');
      console.log('Token exists:', !!token);
      
      // Build URL with format query parameter
      const url = format === 'a4-lined' 
        ? `${API_BASE_URL}/subjective-papers/${paper.id}/download?format=a4-lined`
        : `${API_BASE_URL}/subjective-papers/${paper.id}/download`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('Download response status:', response.status);
      console.log('Response headers:', response.headers.get('content-type'));
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('Download error:', errorData);
        throw new Error(errorData.message || 'Failed to download paper');
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      
      // Filename with format suffix
      const filenameSuffix = format === 'a4-lined' ? '_A4_Lined' : '';
      a.download = `${paper.title.replace(/[^a-z0-9]/gi, '_')}${filenameSuffix}.docx`;
      
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Revoke after the browser has started the download. Doing it in the
      // same turn can cancel the file save.
      setTimeout(() => window.URL.revokeObjectURL(downloadUrl), 1000);

      setMessage({ show: true, text: 'Paper downloaded successfully!', type: 'success' });
      closeDownloadMenu();
    } catch (error) {
      console.error('Error downloading paper:', error);
      setMessage({ show: true, text: error.message || 'Failed to download paper', type: 'error' });
    }
  };

  // Get unique values for filters
  const uniqueClasses = [...new Set(allPapers.map(p => p.class))].sort();
  const uniqueSubjects = [...new Set(allPapers.map(p => p.subject))].sort();
  const uniqueTeachers = [...new Set(allPapers.map(p => p.creator_name || p.created_by))].sort();

  const filteredPapers = papers.filter(paper => {
    // Search term filter
    const matchesSearch = !searchTerm || 
      paper.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      paper.class.toLowerCase().includes(searchTerm.toLowerCase()) ||
      paper.subject.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Class filter
    const matchesClass = !filterClass || paper.class === filterClass;
    
    // Subject filter
    const matchesSubject = !filterSubject || paper.subject === filterSubject;
    
    // Teacher filter
    const matchesTeacher = !filterTeacher || 
      (paper.creator_name || paper.created_by) === filterTeacher;
    
    // Today filter (filter by paper date, not created_at)
    let matchesToday = true;
    if (filterMode === 'today') {
      if (!paper.date) {
        matchesToday = false;
      } else {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const paperDate = new Date(paper.date);
        paperDate.setHours(0, 0, 0, 0);
        matchesToday = paperDate.getTime() === today.getTime();
      }
    }
    
    return matchesSearch && matchesClass && matchesSubject && matchesTeacher && matchesToday;
  });

  return (
    <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
      <MessageBox
        message={message}
        onClose={() => setMessage({ show: false, text: '', type: 'info' })}
      />

      {confirm.show && (
        <ConfirmDialog
          confirm={confirm}
          onConfirm={confirm.onConfirm}
          onCancel={() => setConfirm({ show: false, text: '', onConfirm: null })}
        />
      )}

      {/* Header Section */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '12px'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '10px',
            background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
          }}>
            <i className="fas fa-file-alt" style={{ color: 'white', fontSize: '20px' }}></i>
          </div>
          <div style={{ flex: 1 }}>
            <h1 style={{ 
              color: 'var(--text-primary)', 
              margin: 0,
              fontSize: '1.3rem',
              fontWeight: '700'
            }}>Saved Papers</h1>
            <p style={{
              margin: 0,
              color: 'var(--text-muted)',
              fontSize: '0.8rem'
            }}>Browse and manage your saved subjective papers</p>
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(37, 99, 235, 0.05))', padding: '5px', borderRadius: '8px', border: '1px solid rgba(59, 130, 246, 0.2)' }}>
            <button
              onClick={() => setFilterMode('all')}
              style={{
                padding: '8px 16px',
                border: 'none',
                borderRadius: 6,
                cursor: 'pointer',
                fontWeight: 700,
                fontSize: 13,
                background: filterMode === 'all' ? 'linear-gradient(135deg, #3B82F6, #2563EB)' : 'transparent',
                color: filterMode === 'all' ? 'white' : 'var(--text-primary)',
                transition: 'all 0.3s',
                boxShadow: filterMode === 'all' ? '0 2px 8px rgba(59, 130, 246, 0.3)' : 'none'
              }}
            >
              All Papers
            </button>
            <button
              onClick={() => setFilterMode('today')}
              style={{
                padding: '8px 16px',
                border: 'none',
                borderRadius: 6,
                cursor: 'pointer',
                fontWeight: 700,
                fontSize: 13,
                background: filterMode === 'today' ? 'linear-gradient(135deg, #3B82F6, #2563EB)' : 'transparent',
                color: filterMode === 'today' ? 'white' : 'var(--text-primary)',
                transition: 'all 0.3s',
                boxShadow: filterMode === 'today' ? '0 2px 8px rgba(59, 130, 246, 0.3)' : 'none'
              }}
            >
              Today's Papers
            </button>
          </div>
          <input
            type="text"
            placeholder="Search papers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              padding: '10px 16px',
              border: '1px solid var(--border, #ddd)',
              borderRadius: '8px',
              fontSize: '14px',
              width: '300px'
            }}
          />
        </div>

        {/* Filters Section */}
        <div style={{
          background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.05), rgba(147, 51, 234, 0.05))',
          borderLeft: '3px solid #3B82F6',
          borderRadius: '10px',
          padding: '12px 16px',
          display: 'flex',
          gap: 12,
          alignItems: 'center',
          flexWrap: 'wrap'
        }}>
          {/* Class Filter */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <i className="fas fa-graduation-cap" style={{ color: '#3B82F6', fontSize: '1.1rem' }}></i>
            <label style={{ fontSize: '0.875rem', fontWeight: 700, color: 'var(--text-primary)' }}>Class:</label>
            <select
              value={filterClass}
              onChange={(e) => setFilterClass(e.target.value)}
              style={{
                padding: '8px 12px',
                border: '1px solid var(--border)',
                borderRadius: 8,
                fontSize: 14,
                cursor: 'pointer',
                minWidth: 120,
                background: 'var(--surface, #ffffff)'
              }}
            >
              <option value="">All Classes</option>
              {uniqueClasses.map(cls => (
                <option key={cls} value={cls}>{cls}</option>
              ))}
            </select>
          </div>

          <div style={{ width: '1px', height: '30px', background: 'var(--border, #ddd)' }}></div>

          {/* Subject Filter */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <i className="fas fa-book" style={{ color: '#10b981', fontSize: '1.1rem' }}></i>
            <label style={{ fontSize: '0.875rem', fontWeight: 700, color: 'var(--text-primary)' }}>Subject:</label>
            <select
              value={filterSubject}
              onChange={(e) => setFilterSubject(e.target.value)}
              style={{
                padding: '8px 12px',
                border: '1px solid var(--border)',
                borderRadius: 8,
                fontSize: 14,
                cursor: 'pointer',
                minWidth: 150,
                background: 'var(--surface, #ffffff)'
              }}
            >
              <option value="">All Subjects</option>
              {uniqueSubjects.map(subject => (
                <option key={subject} value={subject}>{subject}</option>
              ))}
            </select>
          </div>

          <div style={{ width: '1px', height: '30px', background: 'var(--border, #ddd)' }}></div>

          {/* Teacher Filter */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <i className="fas fa-user-tie" style={{ color: '#8B5CF6', fontSize: '1.1rem' }}></i>
            <label style={{ fontSize: '0.875rem', fontWeight: 700, color: 'var(--text-primary)' }}>Teacher:</label>
            <select
              value={filterTeacher}
              onChange={(e) => setFilterTeacher(e.target.value)}
              style={{
                padding: '8px 12px',
                border: '1px solid var(--border)',
                borderRadius: 8,
                fontSize: 14,
                cursor: 'pointer',
                minWidth: 150,
                background: 'var(--surface, #ffffff)'
              }}
            >
              <option value="">All Teachers</option>
              {uniqueTeachers.map(teacher => (
                <option key={teacher} value={teacher}>{teacher}</option>
              ))}
            </select>
          </div>

          {/* Clear All Filters */}
          {(filterClass || filterSubject || filterTeacher) && (
            <button
              onClick={() => { setFilterClass(''); setFilterSubject(''); setFilterTeacher(''); }}
              style={{
                padding: '8px 16px',
                border: 'none',
                borderRadius: 8,
                background: 'linear-gradient(135deg, #F59E0B, #D97706)',
                color: 'white',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                boxShadow: '0 2px 6px rgba(245, 158, 11, 0.3)'
              }}
            >
              <i className="fas fa-filter-circle-xmark"></i>
              Clear All
            </button>
          )}
        </div>
      </div>

      {loading && papers.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
          Loading papers...
        </div>
      ) : filteredPapers.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
          {(searchTerm || filterClass || filterSubject || filterTeacher || filterMode === 'today') 
            ? (filterMode === 'today' ? "No papers with today's date" : 'No papers match your filters')
            : 'No saved papers yet'}
          {(filterClass || filterSubject || filterTeacher || filterMode === 'today') && (
            <div style={{ marginTop: '12px' }}>
              <button
                onClick={() => { setFilterClass(''); setFilterSubject(''); setFilterTeacher(''); setSearchTerm(''); setFilterMode('all'); }}
                style={{
                  padding: '8px 18px',
                  fontSize: 13,
                  borderRadius: 6,
                  border: 'none',
                  background: 'linear-gradient(135deg, #3B82F6, #2563EB)',
                  color: 'white',
                  cursor: 'pointer',
                  fontWeight: 600,
                }}
              >
                ← Clear All Filters
              </button>
            </div>
          )}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '20px', alignItems: 'start', overflow: 'visible' }}>
          {filteredPapers.map((paper) => (
            <div
              key={paper.id}
              style={{
                background: 'var(--panel-bg, #ffffff)',
                border: '1px solid var(--border, #e0e0e0)',
                borderRadius: '12px',
                padding: '20px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                transition: 'box-shadow 0.2s ease',
                cursor: 'pointer',
                overflow: 'visible',
                position: 'relative'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.12)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)';
              }}
            >
              <h3 style={{ marginBottom: '12px', color: 'var(--text-primary)', fontSize: '18px' }}>
                {paper.title}
              </h3>
              
              <div style={{ marginBottom: '12px', fontSize: '14px', color: 'var(--text-secondary)' }}>
                <div><strong>Class:</strong> {paper.class}</div>
                <div><strong>Subject:</strong> {paper.subject}</div>
                <div><strong>Paper Date:</strong> {paper.date ? new Date(paper.date).toLocaleDateString() : 'Not set'}</div>
                <div><strong>Created by:</strong> {paper.creator_name || paper.created_by}</div>
              </div>

              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button
                  onClick={() => handleView(paper)}
                  style={{
                    padding: '8px 12px',
                    background: '#3B82F6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <i className="fa-solid fa-eye"></i>
                  View
                </button>

                <button
                  onClick={() => handleEdit(paper)}
                  style={{
                    padding: '8px 12px',
                    background: '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <i className="fa-solid fa-edit"></i>
                  Edit
                </button>

                {/* Download dropdown button */}
                <div ref={downloadDropdownOpen === paper.id ? downloadButtonRef : null} style={{ position: 'relative' }}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      openDownloadMenu(paper.id, e.currentTarget);
                    }}
                    style={{
                      padding: '8px 12px',
                      background: '#8b5cf6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    <i className="fa-solid fa-download"></i>
                    Download
                    <i className="fa-solid fa-chevron-down" style={{ fontSize: '10px' }}></i>
                  </button>
                </div>

                <button
                  onClick={() => handleDelete(paper)}
                  style={{
                    padding: '8px 12px',
                    background: '#ef4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <i className="fa-solid fa-trash"></i>
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {downloadDropdownOpen !== null && downloadMenuPos && createPortal(
        <div
          ref={downloadMenuRef}
          style={{
            position: 'fixed',
            top: downloadMenuPos.top,
            left: downloadMenuPos.left,
            background: 'white',
            border: '1px solid #ddd',
            borderRadius: '6px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 2000,
            minWidth: '180px',
            overflow: 'hidden'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            onClick={() => {
              const paper = filteredPapers.find((item) => item.id === downloadDropdownOpen);
              if (paper) handleDownload(paper, 'legal');
            }}
            style={{
              padding: '10px 16px',
              cursor: 'pointer',
              fontSize: '13px',
              color: '#333',
              borderBottom: '1px solid #eee',
              transition: 'background 0.2s'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#f5f5f5'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'white'; }}
          >
            Legal Format
          </div>
          <div
            onClick={() => {
              const paper = filteredPapers.find((item) => item.id === downloadDropdownOpen);
              if (paper) handleDownload(paper, 'a4-lined');
            }}
            style={{
              padding: '10px 16px',
              cursor: 'pointer',
              fontSize: '13px',
              color: '#333',
              transition: 'background 0.2s'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#f5f5f5'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'white'; }}
          >
            A4 Lined Format
          </div>
        </div>,
        document.body
      )}

      {/* View/Edit Modal */}
      {selectedPaper && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: 'var(--panel-bg, #ffffff)',
            borderRadius: '12px',
            maxWidth: '900px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            padding: '24px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
              <h2>{isEditing ? 'Edit Paper' : 'View Paper'}</h2>
              <button
                onClick={() => { setSelectedPaper(null); setIsEditing(false); }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: 'var(--text-secondary)'
                }}
              >
                ×
              </button>
            </div>

            {isEditing ? (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, fontSize: '14px' }}>
                      Paper Title *
                    </label>
                    <input
                      type="text"
                      value={editFormData.title}
                      onChange={(e) => setEditFormData({...editFormData, title: e.target.value})}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid var(--border, #ddd)',
                        borderRadius: '6px',
                        fontSize: '14px'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, fontSize: '14px' }}>
                      Class *
                    </label>
                    <input
                      type="text"
                      value={editFormData.class}
                      onChange={(e) => setEditFormData({...editFormData, class: e.target.value})}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid var(--border, #ddd)',
                        borderRadius: '6px',
                        fontSize: '14px'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, fontSize: '14px' }}>
                      Date *
                    </label>
                    <input
                      type="date"
                      value={editFormData.date}
                      onChange={(e) => setEditFormData({...editFormData, date: e.target.value})}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid var(--border, #ddd)',
                        borderRadius: '6px',
                        fontSize: '14px',
                        cursor: 'pointer'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, fontSize: '14px' }}>
                      Subject *
                    </label>
                    <input
                      type="text"
                      value={editFormData.subject}
                      onChange={(e) => setEditFormData({...editFormData, subject: e.target.value})}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid var(--border, #ddd)',
                        borderRadius: '6px',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, fontSize: '14px' }}>
                    Content *
                  </label>
                  <Editor
                    apiKey="8h1sqec5a1s49emfs5ctml6o6vqo35fz2kta2qmnf602zhfa"  // Replace with your actual TinyMCE API key
                    onInit={(evt, editor) => editorRef.current = editor}
                    value={editFormData.content}
                    onEditorChange={(content) => setEditFormData({...editFormData, content})}
                    init={{
                      height: 400,
                      menubar: true,
                      plugins: [
                        'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                        'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                        'insertdatetime', 'media', 'table', 'help', 'wordcount'
                      ],
                      toolbar: 'undo redo | blocks | bold italic underline | alignleft aligncenter alignright | bullist numlist | image',
                      content_style: "body { font-family:'Noto Nastaliq Urdu',Arial,sans-serif; font-size:12pt; direction:rtl; }",
                      paste_data_images: true,
                      automatic_uploads: true,
                      images_upload_handler: (blobInfo) => new Promise((resolve, reject) => {
                        const reader = new FileReader();
                        reader.readAsDataURL(blobInfo.blob());
                        reader.onloadend = () => resolve(reader.result);
                        reader.onerror = () => reject('Error reading file');
                      }),
                      paste_preprocess: async (plugin, args) => {
                        const parser = new DOMParser();
                        const doc = parser.parseFromString(args.content, 'text/html');
                        const images = doc.querySelectorAll('img');
                        
                        for (const img of images) {
                          const src = img.getAttribute('src');
                          if (src && (src.startsWith('http://') || src.startsWith('https://'))) {
                            try {
                              const response = await fetch(src);
                              const blob = await response.blob();
                              const reader = new FileReader();
                              const base64 = await new Promise((resolve) => {
                                reader.onloadend = () => resolve(reader.result);
                                reader.readAsDataURL(blob);
                              });
                              img.setAttribute('src', base64);
                            } catch (error) {
                              console.error('Failed to convert external image to base64:', error);
                            }
                          }
                        }
                        
                        args.content = doc.body.innerHTML;
                      }
                    }}
                  />
                </div>

                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => { setIsEditing(false); setSelectedPaper(null); }}
                    style={{
                      padding: '10px 20px',
                      background: '#6b7280',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: 600
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveEdit}
                    disabled={loading}
                    style={{
                      padding: '10px 20px',
                      background: loading ? '#ccc' : '#10b981',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      fontWeight: 600
                    }}
                  >
                    {loading ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div style={{ marginBottom: '20px', padding: '16px', background: 'var(--surface, #f5f5f5)', borderRadius: '8px' }}>
                  <div style={{ marginBottom: '8px' }}><strong>Class:</strong> {selectedPaper.class}</div>
                  <div style={{ marginBottom: '8px' }}><strong>Subject:</strong> {selectedPaper.subject}</div>
                  <div style={{ marginBottom: '8px' }}><strong>Paper Date:</strong> {selectedPaper.date ? new Date(selectedPaper.date).toLocaleDateString() : 'Not set'}</div>
                  <div style={{ marginBottom: '8px' }}><strong>Created by:</strong> {selectedPaper.creator_name || selectedPaper.created_by}</div>
                  <div><strong>Created at:</strong> {new Date(selectedPaper.created_at).toLocaleDateString()}</div>
                </div>

                <div 
                  style={{ 
                    padding: '20px',
                    border: '1px solid var(--border, #ddd)',
                    borderRadius: '8px',
                    minHeight: '300px',
                    background: 'white'
                  }}
                  dangerouslySetInnerHTML={{ __html: selectedPaper.content }}
                />
              </>
            )}
          </div>
        </div>
      )}

    </div>
  );
}
